import { Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal, RevealLines } from "@/components/Reveal";
import { CtaBand } from "@/components/CtaBand";
import { SERVICE_PAGES } from "@/config/services";

const STEPS = [
  { t: "Erstgespräch", d: "Sie schildern Ihr Vorhaben – per WhatsApp, telefonisch oder direkt vor Ort." },
  { t: "Beratung", d: "Wir sehen uns die Fläche an und klären Möglichkeiten, Materialien und Rahmen." },
  { t: "Planung", d: "Sie erhalten ein konkretes Konzept mit Aufbau, Bepflanzung und Ablauf." },
  { t: "Umsetzung", d: "Fachgerechte Ausführung mit sauberer Baustelle und klarer Kommunikation." },
  { t: "Übergabe", d: "Abnahme, Pflegehinweise – auf Wunsch dauerhafte Betreuung." },
];

export default function ServicePage({ slug }) {
  const s = SERVICE_PAGES[slug];
  const others = Object.entries(SERVICE_PAGES).filter(([k]) => k !== slug);

  return (
    <>
      <Seo title={s.seoTitle} description={s.seoDescription} path={s.path} image={s.image} />
      <PageHero
        eyebrow={s.eyebrow}
        titleLines={s.titleLines}
        text={s.intro}
        image={s.image}
        breadcrumb={s.breadcrumb}
      />

      <section className="bg-[#F3EDE2] py-24 md:py-36">
        <div className="mx-auto max-w-[1480px] px-5 md:px-10">
          <div className="grid gap-16 lg:grid-cols-[1.05fr_0.95fr] lg:gap-24">
            <div>
              {s.blocks.map((b, i) => (
                <Reveal key={b.t} delay={i * 0.06}>
                  <div className="border-b border-[#17251C]/12 py-8">
                    <span className="text-[0.62rem] font-bold uppercase tracking-[0.3em] text-[#B4623C]">
                      0{i + 1}
                    </span>
                    <h2 className="display-xl mt-4 text-2xl text-[#17251C] md:text-3xl">{b.t}</h2>
                    <p className="mt-3 max-w-xl text-base leading-relaxed text-[#6E7A69]">{b.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            <div>
              <Reveal className="overflow-hidden arch-mask">
                <img
                  src={s.image}
                  alt={`${s.breadcrumb} in Bremen – Arbeit von Hanse Garten Bremen`}
                  className="aspect-[4/5] w-full object-cover"
                />
              </Reveal>
              <Reveal delay={0.15}>
                <ul className="mt-10 grid grid-cols-2 gap-3">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm font-medium text-[#2F3D2E]">
                      <Check size={14} className="mt-[3px] shrink-0 text-[#B4623C]" />
                      {b}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#2F3D2E] py-24 md:py-32">
        <div className="mx-auto max-w-[1480px] px-5 md:px-10">
          <p className="eyebrow !text-[#DCCDB4]">Ablauf</p>
          <RevealLines
            lines={["So arbeiten wir."]}
            as="h2"
            className="display-xl mt-5 text-[10vw] leading-[0.92] text-[#F3EDE2] md:text-[3.6vw]"
          />
          <div className="mt-16 grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-5">
            {STEPS.map((st, i) => (
              <Reveal key={st.t} delay={i * 0.07}>
                <span className="text-[0.62rem] font-bold uppercase tracking-[0.3em] text-[#B4623C]">0{i + 1}</span>
                <h3 className="display-xl mt-4 text-xl text-[#F3EDE2]">{st.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#B8C3B2]">{st.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#DCCDB4]/30 py-20 md:py-28">
        <div className="mx-auto max-w-[1480px] px-5 md:px-10">
          <p className="eyebrow">Weitere Leistungen</p>
          <div className="mt-8 border-t border-[#17251C]/14">
            {others.map(([k, o]) => (
              <Link
                key={k}
                to={o.path}
                data-testid={`related-service-${k}`}
                className="group flex items-center justify-between gap-6 border-b border-[#17251C]/14 py-6 transition-colors duration-500 hover:bg-[#F3EDE2]/60"
              >
                <span className="display-xl text-xl text-[#17251C] transition-transform duration-500 group-hover:translate-x-2 md:text-3xl">
                  {o.breadcrumb}
                </span>
                <ArrowUpRight
                  size={20}
                  className="shrink-0 text-[#B4623C] transition-transform duration-500 group-hover:-translate-y-1 group-hover:translate-x-1"
                />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
